import React, { useEffect, useState } from "react";
import {
  useLogger,
  DeviceLabels,
} from "amazon-chime-sdk-component-library-react";
import { useDevices } from "../providers/DevicesProvider";
import { logVideoRoomError } from "../utils/logVideoRoomError";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";
import { AUDIO_CONSTRAINTS } from "../utils/audioConstraints";

export const DevicePermissionInitializer: React.FC = () => {
  const logger = useLogger();
  const {
    listAndSelectDevices,
    setPermissionDenied,
    setAudioPermissionGuidance,
  } = useDevices();
  const [permissionsRequested, setPermissionsRequested] = useState(false);

  useEffect(() => {
    const requestPermissionsDirectly = async () => {
      if (permissionsRequested) {
        return;
      }

      try {
        setPermissionsRequested(true);

        // Request camera + mic permissions with a cascading fallback:
        // 1. Combined request (happy path — one prompt on Chrome/Safari)
        // 2. Combined with bare audio (constraint rejection fallback)
        // 3. Split requests (DuckDuckGo iOS — combined throws NotAllowedError
        //    when audio is denied, killing the entire call including video)
        // 4. Audio track check (browsers that return video-only streams)
        let stream: MediaStream | null = null;
        let audioGranted = false;

        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: AUDIO_CONSTRAINTS,
          });
          if (stream.getAudioTracks().length > 0) audioGranted = true;
        } catch {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: true,
            });
            if (stream.getAudioTracks().length > 0) audioGranted = true;
          } catch {
            // Combined call failed entirely — split requests
            try {
              stream = await navigator.mediaDevices.getUserMedia({ video: true });
            } catch {
              // Video denied
            }
            try {
              const audioStream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
              audioStream.getTracks().forEach(t => t.stop());
              audioGranted = true;
            } catch {
              try {
                const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                audioStream.getTracks().forEach(t => t.stop());
                audioGranted = true;
              } catch {
                // Audio denied
              }
            }
          }
        }

        // Safety net: some browsers return a video-only stream without throwing
        if (stream && stream.getAudioTracks().length === 0 && !audioGranted) {
          try {
            const audioStream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
            audioStream.getTracks().forEach(t => t.stop());
            audioGranted = true;
          } catch {
            try {
              const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
              audioStream.getTracks().forEach(t => t.stop());
              audioGranted = true;
            } catch {
              // Audio permission truly unavailable
            }
          }
        }

        // Some iOS browsers (DuckDuckGo) silently block getUserMedia({ audio })
        // without triggering the system microphone permission prompt.
        // Show guidance to enable microphone in system settings.
        if (stream !== null && !audioGranted) {
          setAudioPermissionGuidance('mic-blocked');
        }

        stream?.getTracks().forEach(t => t.stop());

        // Retry-based enumeration: poll until devices with labels appear
        const MAX_RETRIES = 3;
        const RETRY_INTERVAL_MS = 200;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          await new Promise((r) => setTimeout(r, RETRY_INTERVAL_MS));
          try {
            await listAndSelectDevices(DeviceLabels.AudioAndVideo);

            // Check if we got labelled devices (labels require permission)
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasLabels = devices.some((d) => d.label && d.label.trim() !== "");
            if (hasLabels) break;
          } catch (err) {
            logger.error(`Error in device initialization (attempt ${attempt + 1}): ${err}`);
            if (attempt === MAX_RETRIES - 1) {
              logVideoRoomError(
                "Device initialization failed after retries",
                "device_init_retry_exhausted",
                { attempts: MAX_RETRIES, error: String(err) }
              );
            }
          }
        }

        // Auto-selection is handled by DeviceManager.listAndSelectDevices
        // which picks the first available device when none is selected.

      } catch (err) {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          setPermissionDenied(true);
        }
        logger.error(`Error requesting device permissions: ${err}`);
        logVideoRoomError(
          "Device permission request failed",
          err instanceof DOMException ? err.name : "permission_error",
          { error: String(err) }
        );
        // Log device permission denied event for quality monitoring
        logMeetingEvent({
          event_type: 'device_permission_denied',
          meeting_id: new URLSearchParams(window.location.search).get('meeting_id'),
          user: null,
          permission_type: 'audio_video',
          error_name: err instanceof DOMException ? err.name : 'unknown',
          browser: navigator.userAgent,
        });

        // Still enumerate devices so isDevicesLoading becomes false
        // and the UI can show the appropriate error message
        try {
          await listAndSelectDevices(DeviceLabels.AudioAndVideo);
        } catch {
          // Non-fatal: device enumeration may fail without permissions
        }
      }
    };

    // Call immediately when component mounts
    requestPermissionsDirectly();
  }, [
    permissionsRequested,
    listAndSelectDevices,
    logger,
  ]);

  // This component doesn't render anything visual
  return null;
};
